// ONE DAY OF A REP'S WORK, LAID OUT LIKE A CALENDAR — every timestamped
// thing the Activity page already holds for the member, placed on a
// morning-to-evening column with the idle stretches between them measured.
// The density chart says WHICH HOURS were busy; this says WHAT HAPPENED AND
// WHAT DID NOT, minute by minute. Pure: no React, no network, node-tested in
// scripts/test-dayTimeline.mjs.
//
// Two sources, exactly as the page's tiles: the HubSpot mirror's ledger rows
// (calls with their duration, meetings with their span, emails sent, notes,
// tasks created and completed, deals created) and the workspace events
// ledger (operation runs, questions, drafts copied, HubSpot opened,
// sign-ins). Inbound email is left out on purpose — a prospect replying is
// not the rep working — as the density chart already does.
//
// WHAT THIS REFUSES TO DO: draw a quiet day over a missing read. Every
// source reports its own state (ok / loading / unavailable / trimmed), and
// the caller prints those notes beside the column, so an empty column with
// "HubSpot activity unavailable" under it is never mistaken for a day off.
import {
  CHICAGO_TZ,
  whenMs,
  chicagoDayOf,
  isImpersonated,
  runOutcome,
  RUN_OUTCOME_LABELS,
  OPERATION_LABELS,
} from './activityMetrics.js'
import { callHeadline } from './callContent.js'

// The kinds a block can be, in legend order. `hub` says which source a kind
// comes from, so the notes can say which half of the day is missing.
export const TIMELINE_KINDS = [
  { key: 'call', label: 'Calls', source: 'hubspot' },
  { key: 'meeting', label: 'Meetings', source: 'hubspot' },
  { key: 'email', label: 'Emails sent', source: 'hubspot' },
  { key: 'note', label: 'Notes', source: 'hubspot' },
  { key: 'task', label: 'Tasks', source: 'hubspot' },
  { key: 'deal', label: 'Deals', source: 'hubspot' },
  { key: 'run', label: 'Operations run', source: 'ledger' },
  { key: 'question', label: 'Questions asked', source: 'ledger' },
  { key: 'copy', label: 'Drafts copied', source: 'ledger' },
  { key: 'open', label: 'HubSpot opened', source: 'ledger' },
  { key: 'signin', label: 'Sign-ins', source: 'ledger' },
]
export const TIMELINE_KIND_LABELS = Object.fromEntries(TIMELINE_KINDS.map((k) => [k.key, k.label]))

// The column always shows at least the working day, 7 AM to 6 PM Chicago,
// and grows to the hour before the first thing and the hour after the last.
export const DAY_START_MIN = 7 * 60
export const DAY_END_MIN = 18 * 60
// An idle stretch shorter than this is the rep moving between two things,
// not a gap worth naming.
export const GAP_MIN_MINUTES = 15
// A meeting HubSpot holds no end time for is drawn this long and flagged
// `assumed`, so the caller can say the end is a guess.
export const ASSUMED_MEETING_MINUTES = 30
// The ledger events a timeline needs, on top of the page's eager reads.
export const TIMELINE_EVENT_TYPES = ['operation_run', 'draft_copied', 'hubspot_email_opened', 'question_asked', 'sign_in']

const CHI_HMS_FMT = new Intl.DateTimeFormat('en-US', {
  timeZone: CHICAGO_TZ,
  hourCycle: 'h23',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
})

// Minutes past midnight on the Chicago clock (fractional seconds kept), or
// null when the value is not a time. Read off Intl rather than computed from
// an offset so the two DST days a year place their hours where the clock did.
export function chicagoMinuteOfDay(value) {
  const ms = whenMs(value)
  if (ms == null) return null
  let h = 0
  let m = 0
  let s = 0
  try {
    for (const part of CHI_HMS_FMT.formatToParts(new Date(ms))) {
      if (part.type === 'hour') h = Number(part.value)
      else if (part.type === 'minute') m = Number(part.value)
      else if (part.type === 'second') s = Number(part.value)
    }
  } catch {
    return null
  }
  if (h === 24) h = 0
  return h * 60 + m + s / 60
}

// 638.05 → '10:38 AM'
export function clockLabel(min) {
  const total = Math.max(0, Math.floor(Number(min) || 0))
  const h24 = Math.floor(total / 60) % 24
  const mm = total % 60
  const h12 = h24 % 12 || 12
  return `${h12}:${String(mm).padStart(2, '0')} ${h24 < 12 ? 'AM' : 'PM'}`
}

// 13 → '1 PM'
export function hourLabel(hour) {
  const h = ((Math.trunc(Number(hour) || 0) % 24) + 24) % 24
  return `${h % 12 || 12} ${h < 12 ? 'AM' : 'PM'}`
}

// 100 → '1h 40m', 25 → '25m', 0.7 → '1m' (never '0m' for something real)
export function minutesLabel(min) {
  const n = Number(min) || 0
  const m = n > 0 && n < 1 ? 1 : Math.round(n)
  if (m < 60) return `${m}m`
  const h = Math.floor(m / 60)
  const rest = m % 60
  return rest ? `${h}h ${rest}m` : `${h}h`
}

function text(value) {
  return value == null ? '' : String(value)
}

function dayOfMs(value) {
  return chicagoDayOf(value)
}

// Build the blocks for one day. Every block: { key, kind, at, startMin,
// endMin, durationMin, label, detail, link, assumed }. startMin/endMin are
// minutes past Chicago midnight; a point event has endMin === startMin.
function collectBlocks({ day, rows, events }) {
  const blocks = []
  const push = (kind, at, opts) => {
    if (dayOfMs(at) !== day) return
    const startMin = chicagoMinuteOfDay(at)
    if (startMin == null) return
    const durationMin = Math.max(0, Number(opts.durationMin) || 0)
    blocks.push({
      key: `${kind}:${opts.id}`,
      kind,
      at: whenMs(at),
      startMin,
      endMin: Math.min(1440, startMin + durationMin),
      durationMin,
      label: text(opts.label).trim() || TIMELINE_KIND_LABELS[kind],
      detail: text(opts.detail).trim(),
      link: opts.link || null,
      assumed: Boolean(opts.assumed),
    })
  }

  if (rows) {
    for (const r of rows.calls || []) {
      const ms = r.duration_ms != null && r.duration_ms !== '' ? Number(r.duration_ms) : NaN
      push('call', r.at, {
        id: r.id,
        durationMin: Number.isFinite(ms) && ms > 0 ? ms / 60000 : 0,
        label: r.title || (r.company_name ? `Call · ${r.company_name}` : 'Call'),
        detail: [r.company_name && r.title ? r.company_name : '', callHeadline(r)].filter(Boolean).join(' · '),
        link: { detail: 'calls', call: text(r.id) },
      })
    }
    for (const r of rows.meetings || []) {
      const start = r.starts_at || r.at
      const startMin = chicagoMinuteOfDay(start)
      let durationMin = ASSUMED_MEETING_MINUTES
      let assumed = true
      if (r.ends_at && startMin != null && dayOfMs(r.ends_at) === day) {
        const endMin = chicagoMinuteOfDay(r.ends_at)
        if (endMin != null && endMin >= startMin) {
          durationMin = endMin - startMin
          assumed = false
        }
      }
      push('meeting', start, {
        id: r.id,
        durationMin,
        assumed,
        label: r.title || (r.company_name ? `Meeting · ${r.company_name}` : 'Meeting'),
        detail: [r.company_name && r.title ? r.company_name : '', r.outcome || '', assumed ? 'no end time recorded' : ''].filter(Boolean).join(' · '),
        link: { detail: 'meetings' },
      })
    }
    for (const [family, word] of [
      ['outreach', 'Outreach'],
      ['followups', 'Follow-up'],
      ['replies', 'Reply'],
    ]) {
      for (const r of rows[family] || []) {
        // Outreach runs touch 1, 2, 3 … while the company stays silent; the
        // first one needs no number.
        const numbered = (family === 'followups' && r.touch) || (family === 'outreach' && r.touch > 1)
        const touch = numbered ? ` (touch ${r.touch})` : ''
        push('email', r.at, {
          id: `${family}:${r.id}`,
          label: `${word}${touch}: ${r.subject || '(no subject)'}`,
          detail: r.company_name || (Array.isArray(r.to) ? r.to.join(', ') : r.to) || '',
          link: { detail: family },
        })
      }
    }
    for (const r of rows.notes || []) {
      push('note', r.at, {
        id: r.id,
        label: r.company_name ? `Note · ${r.company_name}` : 'Note',
        detail: r.body || '',
        link: { detail: 'notes' },
      })
    }
    for (const r of rows.tasks || []) {
      push('task', r.created_at, {
        id: `created:${r.id}`,
        label: `Task created: ${r.subject || '(untitled)'}`,
        detail: r.company_name || '',
        link: { detail: 'tasks' },
      })
      if (r.completed_at) {
        push('task', r.completed_at, {
          id: `completed:${r.id}`,
          label: `Task completed: ${r.subject || '(untitled)'}`,
          detail: r.company_name || '',
          link: { detail: 'tasks' },
        })
      }
    }
    for (const r of rows.deals || []) {
      push('deal', r.created_at, {
        id: r.id,
        label: `Deal created: ${r.name || '(unnamed)'}`,
        detail: [r.company_name || '', r.stage || ''].filter(Boolean).join(' · '),
        link: { detail: 'deals' },
      })
    }
  }

  const ev = events || {}
  const own = (list) => (Array.isArray(list) ? list.filter((e) => e && !isImpersonated(e)) : [])
  for (const r of own(ev.runs)) {
    const op = r.data && r.data.operationId
    push('run', r.createdAt, {
      id: r.id,
      label: `Ran ${OPERATION_LABELS[op] || 'a daily operation'}`,
      detail: RUN_OUTCOME_LABELS[runOutcome(r)] || '',
      link: { detail: 'runs' },
    })
  }
  for (const r of own(ev.questions)) {
    push('question', r.createdAt, {
      id: r.id,
      label: 'Asked the assistant',
      detail: (r.data && r.data.preview) || r.detail || '',
      link: { detail: 'questions' },
    })
  }
  for (const r of own(ev.copies)) {
    const kind = r.data && r.data.kind
    push('copy', r.createdAt, {
      id: r.id,
      label: kind === 'subject' ? 'Copied a subject' : 'Copied an email',
      detail: (r.data && r.data.company) || '',
      link: { detail: 'copies' },
    })
  }
  for (const r of own(ev.opens)) {
    push('open', r.createdAt, {
      id: r.id,
      label: 'Opened HubSpot',
      detail: (r.data && r.data.company) || '',
      link: { detail: 'hubspot_opens' },
    })
  }
  for (const r of own(ev.signins)) {
    push('signin', r.createdAt, {
      id: r.id,
      label: 'Signed in',
      detail: r.detail || '',
      link: { detail: 'signins' },
    })
  }

  blocks.sort((a, b) => a.startMin - b.startMin || a.endMin - b.endMin || (a.key < b.key ? -1 : a.key > b.key ? 1 : 0))
  return blocks
}

// The notes that keep an empty column honest. `sources` is
// { hubspot: 'ok'|'loading'|'unavailable', ledger: { runs, questions,
// copies, opens, signins: same words } }; anything missing counts as
// unavailable, because a source nobody vouched for is not one to be quiet
// about.
const LEDGER_WORDS = {
  runs: 'operation runs',
  questions: 'questions asked',
  copies: 'drafts copied',
  opens: 'HubSpot opens',
  signins: 'sign-ins',
}
const HUBSPOT_FAMILIES = ['calls', 'meetings', 'outreach', 'followups', 'replies', 'notes', 'tasks', 'deals']
const FAMILY_WORDS = {
  calls: 'calls',
  meetings: 'meetings',
  outreach: 'outreach emails',
  followups: 'follow-ups',
  replies: 'replies sent',
  notes: 'notes',
  tasks: 'tasks',
  deals: 'deals',
}

function sourceNotes({ day, metrics, sources }) {
  const notes = []
  const hub = (sources && sources.hubspot) || 'unavailable'
  if (hub === 'loading') notes.push('HubSpot activity is still loading — calls, meetings, emails, notes, tasks and deals will appear when the mirror answers.')
  else if (hub !== 'ok') notes.push('HubSpot activity is unavailable, so calls, meetings, emails, notes, tasks and deals are missing from this day — it is not shown as quiet.')
  else if (metrics && metrics.rows_truncated) {
    // The mirror trims each list newest-first to its byte budget. A day at or
    // before the oldest row kept may be missing rows of that family.
    const trimmed = []
    for (const family of HUBSPOT_FAMILIES) {
      if (!metrics.rows_truncated[family]) continue
      const list = (metrics.rows && metrics.rows[family]) || []
      let oldest = ''
      for (const r of list) {
        const d = dayOfMs(r.at || r.created_at)
        if (d && (!oldest || d < oldest)) oldest = d
      }
      if (!oldest || day <= oldest) trimmed.push(FAMILY_WORDS[family])
    }
    if (trimmed.length) notes.push(`The mirror trimmed the ${trimmed.join(', ')} list${trimmed.length === 1 ? '' : 's'} to its byte budget (newest first), so this day may be missing some of them.`)
  }
  const ledger = (sources && sources.ledger) || {}
  const missing = []
  const loading = []
  for (const key of Object.keys(LEDGER_WORDS)) {
    const state = ledger[key] || 'unavailable'
    if (state === 'loading') loading.push(LEDGER_WORDS[key])
    else if (state !== 'ok') missing.push(LEDGER_WORDS[key])
  }
  if (loading.length) notes.push(`Still reading the ledger for ${loading.join(', ')}.`)
  if (missing.length) notes.push(`The workspace ledger could not be read for ${missing.join(', ')}, so those are missing from this day.`)
  return notes
}

// The timeline for `day` ('YYYY-MM-DD', Chicago). `metrics` is the /metrics
// body (or null), `events` the page's ledger rows by kind, `sources` their
// states (see sourceNotes), `now` the clock.
export function dayTimeline({ day = '', metrics = null, events = {}, sources = {}, now = Date.now() } = {}) {
  const rows = metrics && metrics.rows && typeof metrics.rows === 'object' ? metrics.rows : null
  const blocks = day ? collectBlocks({ day, rows, events }) : []

  // Busy intervals: the blocks merged wherever they touch or overlap, in
  // order. The gaps are the spaces between them, named only when they are
  // long enough to be a gap and not a breath.
  const busy = []
  for (const b of blocks) {
    const last = busy[busy.length - 1]
    if (last && b.startMin <= last.endMin) last.endMin = Math.max(last.endMin, b.endMin)
    else busy.push({ startMin: b.startMin, endMin: b.endMin })
  }
  const gaps = []
  for (let i = 1; i < busy.length; i += 1) {
    const minutes = busy[i].startMin - busy[i - 1].endMin
    if (minutes >= GAP_MIN_MINUTES) gaps.push({ startMin: busy[i - 1].endMin, endMin: busy[i].startMin, minutes })
  }
  let longestGap = null
  for (const g of gaps) if (!longestGap || g.minutes > longestGap.minutes) longestGap = g
  const idleMin = gaps.reduce((sum, g) => sum + g.minutes, 0)

  const isToday = Boolean(day) && dayOfMs(now) === day
  const nowMin = isToday ? chicagoMinuteOfDay(now) : null
  const firstMin = blocks.length ? blocks[0].startMin : null
  const lastMin = blocks.length ? Math.max(...blocks.map((b) => b.endMin)) : null
  let startMin = DAY_START_MIN
  let endMin = DAY_END_MIN
  if (firstMin != null) startMin = Math.min(startMin, Math.floor(firstMin / 60) * 60)
  if (lastMin != null) endMin = Math.max(endMin, Math.ceil(lastMin / 60) * 60)
  if (nowMin != null) endMin = Math.max(endMin, Math.ceil(nowMin / 60) * 60)
  endMin = Math.min(1440, Math.max(endMin, startMin + 60))

  const counts = {}
  for (const b of blocks) counts[b.kind] = (counts[b.kind] || 0) + 1
  const busyMin = busy.reduce((sum, b) => sum + (b.endMin - b.startMin), 0)

  return {
    day,
    blocks,
    busy,
    gaps,
    longestGap,
    idleMin,
    busyMin,
    bounds: { startMin, endMin },
    isToday,
    nowMin,
    firstMin,
    lastMin,
    counts,
    notes: sourceNotes({ day, metrics, sources }),
  }
}

// The one-line reading of a day, as parts the caller joins with ' · '.
export function dayTimelineSummary(timeline) {
  if (!timeline) return []
  const n = timeline.blocks.length
  if (!n) return [timeline.isToday ? 'Nothing logged yet today' : 'Nothing logged on this day']
  const parts = [
    `First activity ${clockLabel(timeline.firstMin)}`,
    `last ${clockLabel(timeline.lastMin)}`,
    `${n.toLocaleString()} ${n === 1 ? 'activity' : 'activities'}`,
  ]
  if (timeline.longestGap) {
    const g = timeline.longestGap
    parts.push(`longest gap ${minutesLabel(g.minutes)} (${clockLabel(g.startMin)} – ${clockLabel(g.endMin)})`)
  }
  if (timeline.gaps.length > 1) parts.push(`${timeline.gaps.length} gaps of ${GAP_MIN_MINUTES}m or more, ${minutesLabel(timeline.idleMin)} idle in all`)
  else if (timeline.gaps.length === 0 && n > 1) parts.push(`no gap of ${GAP_MIN_MINUTES}m or more between activities`)
  return parts
}

// LANES. Two things at once — a call during a meeting, three emails in one
// minute — must not paint over each other, so overlapping blocks are dealt
// into side-by-side lanes, calendar style. `footprintMin` is the height a
// block occupies for the purpose of overlap (a point event still needs room
// for its label), which is a drawing concern and so a parameter here.
// Returns the blocks with { lane, lanes } added, in the same order.
export function layoutBlocks(blocks, { footprintMin = 12 } = {}) {
  const out = []
  let cluster = [] // blocks in the current overlapping run
  let laneEnds = [] // per lane, the minute the lane is busy until
  let clusterEnd = -Infinity
  const closeCluster = () => {
    const lanes = laneEnds.length
    for (const b of cluster) b.lanes = lanes
    cluster = []
    laneEnds = []
    clusterEnd = -Infinity
  }
  for (const src of blocks || []) {
    const b = { ...src }
    const foot = Math.max(b.endMin, b.startMin + footprintMin)
    if (cluster.length && b.startMin >= clusterEnd) closeCluster()
    let lane = laneEnds.findIndex((end) => end <= b.startMin)
    if (lane < 0) {
      lane = laneEnds.length
      laneEnds.push(foot)
    } else {
      laneEnds[lane] = foot
    }
    b.lane = lane
    cluster.push(b)
    clusterEnd = Math.max(clusterEnd, foot)
    out.push(b)
  }
  if (cluster.length) closeCluster()
  return out
}

// Which day the timeline should open on: the URL's ?day= when it is a real
// day inside the range, else the range's last day (today for every
// range that ends today).
export function timelineDayOf(param, range) {
  const untilDay = (range && range.untilDay) || ''
  const sinceDay = (range && range.sinceDay) || ''
  const wanted = String(param || '').trim()
  if (!/^\d{4}-\d{2}-\d{2}$/.test(wanted)) return untilDay
  if (untilDay && wanted > untilDay) return untilDay
  if (sinceDay && wanted < sinceDay) return sinceDay
  return wanted
}
